// Dummy data types — UI prototype, nothing is wired up.
//
// Operating effectiveness is the argument that a control's design & implementation verdict held for
// the whole period. That argument is a set of basis items, each asserting: this evidence covers this
// span of the period, provided these conditions hold.

/** An ISO calendar date, `YYYY-MM-DD`. */
export type IsoDate = string;

/** An inclusive date range. Spans and periods are both expressed this way. */
export type DateRange = {from: IsoDate; to: IsoDate};

export type Outcome =
  | "operated_effectively"
  | "operated_with_exceptions"
  | "did_not_operate_effectively"
  | "insufficient_evidence";

export const OUTCOME_LABEL: Record<Outcome, string> = {
  operated_effectively: "Operated effectively",
  operated_with_exceptions: "Operated with exceptions",
  did_not_operate_effectively: "Did not operate effectively",
  insufficient_evidence: "Insufficient evidence",
};

export const OUTCOME_COLOR: Record<Outcome, string> = {
  operated_effectively: "green",
  operated_with_exceptions: "yellow",
  did_not_operate_effectively: "red",
  insufficient_evidence: "gray",
};

/** A design or implementation verdict — the subject OE extends across the period. */
export type Verdict = "effective" | "not_effective";

/**
 * A control that isn't split across systems still needs somewhere to hang its verdicts, so it carries
 * a single global scope. There is nothing to choose between, so the scope isn't worth showing.
 */
export const GLOBAL_SCOPE = "Global";
export const isGlobalScope = (scope: string): boolean => scope === GLOBAL_SCOPE;

/**
 * What must be true for a basis item to hold.
 *
 * On a `note` the auditor asserts it, so `met` is authored. On a `control` it is read from that
 * control's OE conclusion — which is what makes an upstream failure propagate without anyone
 * having to remember to untick anything.
 */
export type Condition =
  | {
      kind: "note";
      text: string;
      met: boolean;
      /** Who made the call, and when — a note is somebody's judgement, not a computed fact. */
      assertedBy?: string;
      assertedAt?: string;
    }
  | {kind: "control"; control: string; scope: string};

/**
 * A quoted extract from a source, with the note explaining why it matters. Mirrors the shape of a
 * real `SourceExtract` + its per-use citation context.
 */
export type Citation = {
  quote: string;
  source: string;
  /** Where in the source it came from, e.g. "Section 4.2" or "p. 18". */
  locator?: string;
  /** Why this extract supports the claim. */
  context?: string;
};

/** What a basis item leans on. */
export type Evidence =
  | {
      kind: "test";
      label: string;
      itemsTested: number;
      exceptions: number;
      /** Percentages. Present together or not at all; drives the derived outcome. */
      deviationRate?: number;
      tolerableRate?: number;
      /** The test's own conclusion, so the basis doesn't have to be clicked through to read. */
      outcome?: Outcome;
      conclusion?: string;
    }
  | {
      kind: "work_of_others";
      label: string;
      provider: string;
      reportPeriod?: DateRange;
      /** The provider's opinion or overall finding. */
      opinion?: string;
      /** Extracts from the report supporting what we're leaning on. */
      citations?: Citation[];
    }
  | {
      kind: "prior_conclusion";
      control: string;
      scope: string;
      period: string;
      outcome: Outcome;
      justification?: string;
    };

export const EVIDENCE_LABEL: Record<Evidence["kind"], string> = {
  test: "Test",
  work_of_others: "Work of others",
  prior_conclusion: "Prior conclusion",
};

export type SampleMethod = "full_population" | "random" | "judgemental" | "single_item";

export const SAMPLE_METHOD_LABEL: Record<SampleMethod, string> = {
  full_population: "Full population",
  random: "Random",
  judgemental: "Judgemental",
  single_item: "Single item",
};

/**
 * What the testing engine is handed. Generated from a procedure and the goal rather than authored
 * field by field, but a schema all the same — a test can't be instantiated from a sentence.
 */
export type TestParameters = {
  /** What is being sampled from. */
  population: string;
  method: SampleMethod;
  sampleSize: number;
  criteria: {name: string; guidance: string}[];
  /** The data points to pull off each item before the criteria can be run. */
  fields: {name: string; type: string; guidance: string}[];
};

/**
 * What an item intends to lean on, before the evidence exists. The same three kinds — planning is
 * writing the argument first and filling the evidence in afterwards, not a different sort of thing.
 */
export type PlannedEvidence =
  | {
      kind: "test";
      /**
       * What this test has to establish. Written while the basis is being drafted, so the argument
       * can be read and argued with before anybody works out how to run the test.
       */
      goal: string;
      /** The library procedure it was characterised from, where one was used. */
      procedure?: {id: string; name: string; version: string};
      /** Present once the test has been specified; absent while it is still a stub. */
      parameters?: TestParameters;
    }
  | {kind: "work_of_others"; provider?: string; expectedReport?: string; reportPeriod?: DateRange}
  | {kind: "prior_conclusion"; control: string; scope: string; period: string};

type BasisItemBase = {
  id: string;
  /** The stretch of the period this item claims to cover. Usually the whole of it. */
  span: DateRange;
  conditions: Condition[];
};

/**
 * A planned item says what its evidence will be; an obtained one carries it. Nothing else about the
 * item differs, which is why a plan needs no structure of its own — it is the basis, one state
 * earlier.
 */
export type BasisItem = BasisItemBase &
  ({status: "obtained"; evidence: Evidence} | {status: "planned"; evidence: PlannedEvidence});

export type Conclusion = {outcome: Outcome; supportsReliance: boolean; justification: string};

/** One control at one scope — the grain that actually carries verdicts. */
export type ScopedControl = {
  scope: string;
  design: Verdict;
  implementation: Verdict;
  /** Absent until the control concludes; a condition pointing here then reads "not concluded yet". */
  oe?: Conclusion;
};

/** How often the control runs. Feeds the sample size a test plan proposes. */
export type ControlFrequency = "daily" | "weekly" | "monthly" | "quarterly" | "annually";

export type DemoControl = {
  ref: string;
  title: string;
  /** The process or IT area the control is filed under. */
  assessment: string;
  /** True where the application performs the control itself — what makes ITGC reliance necessary. */
  automated?: boolean;
  frequency?: ControlFrequency;
  scopes: ScopedControl[];
};

/**
 * A declared dependency between two controls: `from` is leaned on by `to`. Declarations live on the
 * control and pre-fill the conditions of basis items; they carry no scope of their own, since a
 * condition records the scope it settled on at the moment it was created.
 */
export type DependencyEdge = {
  from: string;
  to: string;
  provenance: string;
  /**
   * Which scope of `from` is being leaned on, where the two controls aren't scoped alike — a control
   * filed against the process knows which application runs it, so the declaration can say so.
   */
  scope?: string;
};

/** The operating-effectiveness assessment for one control at one scope. */
export type OeAssessment = {
  control: string;
  scope: string;
  period: DateRange;
  basis: BasisItem[];
  conclusion: Conclusion;
  /**
   * Tests already underway when the scenario opens, by the id of the planned item each one belongs
   * to. Keyed here rather than on the item because progress is the engine's, not the basis item's —
   * this is the fixture seeding the engine, and an item id is unique within one assessment.
   */
  underway?: Record<string, {tested: number; exceptions: number}>;
};

/**
 * One situation from the design document, complete enough to drive every demo surface — the OE page,
 * the dependencies tab, the dependency map and the matrix all read the active scenario.
 */
export type Scenario = {
  id: string;
  /** Position in the design document, e.g. "Scenario 2". */
  section: string;
  title: string;
  tagline: string;
  situation: string;
  /** Which control and scope the OE page opens on. */
  focus: {control: string; scope: string};
  oe: OeAssessment[];
  controls: DemoControl[];
  edges: DependencyEdge[];
};

export const controlOf = (scenario: Scenario, ref: string): DemoControl | undefined =>
  scenario.controls.find(c => c.ref === ref);

export const scopedControlOf = (scenario: Scenario, ref: string, scope: string): ScopedControl | undefined =>
  controlOf(scenario, ref)?.scopes.find(s => s.scope === scope);

export const oeOf = (scenario: Scenario, ref: string, scope: string): OeAssessment | undefined =>
  scenario.oe.find(a => a.control === ref && a.scope === scope);
